import React, { Component, createRef } from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Options as MessageActionOptions } from '../../state/actions/approve-items';
import {
  Appearance,
  ContentType,
  Folder,
  HotKeysCodes as HotKeys,
  HotKeysSettings,
  IAdvancedFilters,
  IPageInfo,
  Message,
  Post,
  SpotPolicy,
  User
} from '../../state/types';
import { ModalType } from '../../modals/state/types';
import ListItem from '../list-item';
import { track, TrackingProp } from 'react-tracking';

const mapStateToProps = state => {
  const currentModals = state?.modals?.currentModals || [];
  return { isModalVisible: currentModals.length > 0 };
};

interface State {
  actionType?: 'approve' | 'reject';
  nextCursorId?: string;
}

interface Props {
  itemsList: Message[];
  users: Record<string, User>;
  posts: Record<string, Post>;
  pageInfo: IPageInfo;
  isMultiSelectVisble: boolean;
  isModerationMode: boolean;
  inpageSkin: string;
  searchValue: string;
  filters: IAdvancedFilters;
  isModalVisible: boolean;
  hotKeysSettings: HotKeysSettings;
  spotPolicy: SpotPolicy;
  isMultipleModeratorsMode: boolean;
  tracking: TrackingProp;
  contentType: ContentType;
  spotId: string;
  isHighlightTagsEnabled: boolean;
  openModal: (modalType, modalProps) => void;
  canUseActiveBar: () => boolean;
  openConfirmationModal: (actionType, mode) => void;
  clearAllItems: () => void;
  toggleItem: (props) => void;
  approveItems: (messages: Message[], options: MessageActionOptions) => void;
  rejectItems: (messages: Message[], options: MessageActionOptions) => void;
  setClientAppearance: ({ id: string, appearance: Appearance, folder: Folder }) => void;
  changePageFilters: ({ filters }: { filters: Partial<IAdvancedFilters> }) => void;
  setConversationPolicy: (props) => void;
  setHighlightTag: (props) => void;
  isModerationViewer: boolean;
}

class DashboardList extends Component<Props, State> {
  itemsRef: React.RefObject<HTMLDivElement>[];

  static defaultProps = {
    itemsList: [],
    canUseActiveBar: () => false,
    openConfirmationModal: () => {},
    toggleItem: () => {},
    changePageFilters: () => {},
    setClientAppearance: () => {},
    approveItems: () => {},
    rejectItems: () => {},
    setHighlightTag: () => {}
  };

  state = {
    nextCursorId: null
  };

  constructor(props: Props) {
    super(props);
    this.itemsRef = props.itemsList.map(() => createRef());
  }

  componentDidUpdate(prevProps: Props) {
    const {
      hotKeysSettings: { isModerationBarActive },
      isModerationMode,
      itemsList
    } = this.props;

    if (isModerationBarActive) {
      this.focusNearestElement(prevProps);

      if (isModerationMode && prevProps.itemsList.length < itemsList.length) {
        this.itemsRef = itemsList.map(_ => createRef());
      }
    }
  }

  focusNearestElement(prevProps: Props) {
    const { itemsList, hotKeysSettings } = this.props;
    const { cursor } = hotKeysSettings;
    const { nextCursorId } = this.state;

    const lastCursorItem: Message = prevProps.itemsList[cursor];
    const newCursorItem: Message = itemsList[cursor];
    const isSameItem: boolean =
      lastCursorItem && newCursorItem && lastCursorItem.get('id') === newCursorItem.get('id');

    // Making sure to focus the nearest element after approve / reject action
    if (nextCursorId && !isSameItem) {
      const newCursor: number =
        itemsList.findIndex((item: Message) => item.get('id') === nextCursorId) || cursor;
      if (itemsList[newCursor]) {
        if (itemsList[cursor] && cursor !== newCursor) {
          this.handleCursorChange(cursor, newCursor);
        }
      }
      this.selectItem(newCursor, true);
    }
  }

  getKeyboardAction(keyCode: number, isShiftPressed: boolean): string {
    if (isShiftPressed) {
      if (keyCode === HotKeys.ARROW_UP) {
        return 'GRAB_UP';
      } else if (keyCode === HotKeys.ARROW_DOWN) {
        return 'GRAB_DOWN';
      }
    } else if (keyCode === HotKeys.TAB) {
      return 'NEXT';
    } else if (keyCode === HotKeys.ARROW_UP) {
      return 'UP';
    } else if (keyCode === HotKeys.ARROW_DOWN) {
      return 'DOWN';
    } else if (keyCode === HotKeys.ARROW_RIGHT) {
      return 'APPROVE';
    } else if (keyCode === HotKeys.ARROW_LEFT) {
      return 'REJECT';
    } else if (keyCode === HotKeys.SPACE) {
      return 'SHOW_CONVERSATION';
    } else if (keyCode === HotKeys.ESC) {
      return 'EXIT';
    } else if (keyCode === HotKeys.ENTER) {
      return 'SELECT';
    }
    return 'NONE';
  }

  onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const {
      itemsList,
      hotKeysSettings: { cursor, isModerationBarActive },
      canUseActiveBar,
      clearAllItems,
      isModalVisible
    } = this.props;
    let newCursor = cursor;

    const keyCode: number = e.keyCode;
    const isShiftPressed: boolean = e.shiftKey;
    const canNotUseActiveBar = !canUseActiveBar();
    const BOTTOM = itemsList.length - 1;
    const TOP = 0;

    if (
      canNotUseActiveBar ||
      (!isModerationBarActive && cursor === TOP && keyCode !== HotKeys.TAB)
    ) {
      return;
    }

    e.preventDefault();

    const actionType = this.getKeyboardAction(keyCode, isShiftPressed);
    const prevCursor = cursor === TOP ? BOTTOM : cursor - 1;
    const nextCursor = cursor === BOTTOM ? TOP : cursor + 1;
    const prevItem = itemsList[prevCursor];
    const currItem = itemsList[cursor];
    const nextItem = itemsList[nextCursor];

    switch (actionType) {
      case 'NEXT':
        if (isModerationBarActive) {
          currItem.setChecked(false);
          this.selectItem(nextCursor, true);
          newCursor = nextCursor;
        } else {
          this.selectItem(cursor, true);
        }
        break;
      case 'UP':
        newCursor = prevCursor;
        this.selectItem(prevCursor);
        break;
      case 'DOWN':
        newCursor = nextCursor;
        this.selectItem(nextCursor);
        break;
      case 'APPROVE':
        this.handleApproveOrReject('approve');
        this.setState({ actionType: 'approve' });
        break;
      case 'REJECT':
        this.handleApproveOrReject('reject');
        this.setState({ actionType: 'reject' });
        break;
      case 'SHOW_CONVERSATION':
        this.handleOpenViewConvModal(currItem, true);
        break;
      case 'GRAB_UP':
        newCursor = prevCursor;
        const prevSelection = prevItem.isChecked();
        if (prevSelection) {
          currItem.setChecked(false);
        }
        this.selectItem(prevCursor, !prevSelection);
        break;
      case 'GRAB_DOWN':
        newCursor = nextCursor;
        const nextSelection = nextItem.isChecked();
        if (nextSelection) {
          currItem.setChecked(false);
        }
        this.selectItem(nextCursor, !nextSelection);
        break;
      case 'EXIT':
        if (!isModalVisible) {
          clearAllItems();
        }
        break;
      case 'SELECT':
        const currentSelection = currItem.isChecked();
        this.selectItem(cursor, !currentSelection);
        break;
      default:
      case 'NONE':
        break;
    }
    if (newCursor !== cursor) {
      this.handleCursorChange(cursor, newCursor);
    }
  };

  handleCursorChange(cursor: number, newCursor: number) {
    this.itemsRef[newCursor].current.focus();
    this.itemsRef[newCursor].current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
    this.itemsRef[cursor].current.blur();
  }

  getCheckedItems(): Message[] {
    const { itemsList } = this.props;
    return itemsList.filter(message => message.isChecked());
  }

  handleApproveOrReject(type: 'approve' | 'reject') {
    const {
      openConfirmationModal,
      hotKeysSettings: { cursor },
      itemsList
    } = this.props;
    const checkedItems = this.getCheckedItems();
    const numOfCheckedItems = checkedItems.length;
    const lastCheckedItem = checkedItems.pop();
    const currItem = itemsList[cursor];
    const nextItemIndex = itemsList.lastIndexOf(lastCheckedItem) + 1;
    const nextCursorId =
      nextItemIndex === itemsList.length ? 0 : itemsList[nextItemIndex].get('id');

    if (numOfCheckedItems > 1) {
      openConfirmationModal(type, 'keyboard');
      this.itemsRef[cursor].current.blur();
      this.setState({ nextCursorId });
    } else if (numOfCheckedItems === 1 && currItem.isChecked()) {
      this.handleSingleItem(type);
      this.setState({ nextCursorId });
    }
  }

  handleSingleItem(type: 'approve' | 'reject') {
    const {
      itemsList,
      hotKeysSettings: { cursor },
      approveItems,
      rejectItems
    } = this.props;
    const message = itemsList[cursor];
    if (type === 'approve') {
      approveItems([message], { appearance: Appearance.APPROVED, fromKeyboard: true });
    } else {
      rejectItems([message], { appearance: Appearance.BLOCKED, fromKeyboard: true });
    }
  }

  handleOpenViewConvModal(message: Message, fromKeyboard: boolean = false) {
    const { inpageSkin, contentType, spotId } = this.props;

    this.props.tracking?.trackEvent({ eventName: 'article link clicked', keyboard: fromKeyboard });

    this.props.openModal(ModalType.VIEW_CONVERSATION, {
      spotId,
      message,
      inpageSkin,
      contentType
    });
  }

  selectItem(index: number, checked?: boolean) {
    const { itemsList, toggleItem } = this.props;
    const item = itemsList[index];

    if (item) {
      toggleItem({
        cursor: index,
        id: item.get('id'),
        checked: typeof checked === 'boolean' ? checked : item.isChecked()
      });
    }
  }

  render() {
    const {
      isMultiSelectVisble,
      pageInfo,
      itemsList,
      isModerationMode,
      searchValue,
      hotKeysSettings: { cursor, isModerationBarActive },
      users,
      posts,
      approveItems,
      rejectItems,
      toggleItem,
      setClientAppearance,
      changePageFilters,
      openModal,
      spotPolicy,
      setConversationPolicy,
      isMultipleModeratorsMode,
      setHighlightTag,
      isHighlightTagsEnabled,
      isModerationViewer
    } = this.props;

    const className = classnames('dashboard-list', {
      'checkbox-state': isMultiSelectVisble || isModerationBarActive
    });

    const isModerationButtonEnabled =
      !isModerationViewer &&
      (!isMultipleModeratorsMode || isModerationMode || pageInfo.folder !== Folder.PENDING);

    return (
      <ul className={className} onKeyDown={this.onKeyDown}>
        {itemsList.map((item, index) => (
          <ListItem
            key={item.get('id')}
            position={index}
            activeBarRef={this.itemsRef[index]}
            message={item}
            pageInfo={pageInfo}
            isModerationMode={isModerationMode}
            searchValue={searchValue}
            isModerationBarActive={isModerationBarActive}
            isSelectedItem={isModerationBarActive && cursor === index}
            user={users[item.get('userId')]}
            post={posts[item.get('conversationId')]}
            approveItems={approveItems}
            rejectItems={rejectItems}
            handleSelectItem={toggleItem}
            handleOpenViewConvModal={this.handleOpenViewConvModal.bind(this)}
            setClientAppearance={setClientAppearance}
            changePageFilters={changePageFilters}
            openModal={openModal}
            spotPolicy={spotPolicy}
            setConversationPolicy={setConversationPolicy}
            isModerationButtonEnabled={isModerationButtonEnabled}
            setHighlightTag={setHighlightTag}
            isHighlightTagsEnabled={isHighlightTagsEnabled}
          />
        ))}
      </ul>
    );
  }
}

export default compose(track(), connect(mapStateToProps, null))(DashboardList);
